import VueScrollTo from 'vue-scrollto';
import colors from 'vuetify/lib/util/colors';
import csvDownload from 'json-to-csv-export';
import moment from 'moment';
import _ from 'lodash';

const UtilsPlugin = {};

UtilsPlugin.install = Vue => {
    Vue.prototype.$utils = {};

    Vue.prototype.$utils.resolvePathInObjectWithString = (
        obj,
        path,
        separator = '.',
    ) => {
        const properties = Array.isArray(path) ? path : path.split(separator);
        return properties.reduce((prev, curr) => prev && prev[curr], obj);
    };
    Vue.prototype.$utils.wait = async ms => new Promise(resolve => {
        setTimeout(resolve, ms);
    });
    Vue.prototype.$utils.generateRandomString = (stringLength = 7) => Math.random()
        .toString(36)
        .slice(2)
        .substring(0, stringLength);
    Vue.prototype.$utils.watchDomForMountedElement = (
        selector,
        maxCount = 20,
        checkEveryMs = 100,
    ) => new Promise((resolve, reject) => {
        const waitForEl = (elSelector, count = 0) => {
            const el = document.querySelector(elSelector);
            if (el) resolve(el);
            else {
                setTimeout(() => {
                    const inc = count + 1;
                    if (inc < maxCount) waitForEl(elSelector, inc);
                    else reject();
                }, checkEveryMs);
            }
        };
        waitForEl(selector);
    });
    Vue.prototype.$utils.scrollToId = async (
        id,
        offset = 2,
        idSuffix = 'id_',
    ) => {
        await Vue.prototype.$utils.watchDomForMountedElement(
            `#${idSuffix}${id}`,
        );
        await VueScrollTo.scrollTo(`[id='${idSuffix}${id}']`, 0, {
            container: 'body',
            offset,
            force: true,
            x: true,
            y: true,
        });
        document.getElementById(`${idSuffix}${id}`).classList.add('highlight');
    };
    // Removes the attributes that are empty, null or undefined
    Vue.prototype.$utils.removeEmptyNullOrUndefined = (
        object,
        checkEmptyString = false,
    ) => {
        const tmp = _.cloneDeepWith(object, value => (
            (value instanceof File || value instanceof Blob) ? value : undefined
        ));

        Object.keys(tmp).forEach(key => {
            if (tmp[key] instanceof File || tmp[key] instanceof Blob) {
                return;
            }
            if (checkEmptyString) {
                if (
                    tmp[key] === ''
                    || tmp[key] === null
                    || tmp[key] === undefined
                ) {
                    delete tmp[key];
                }
            } else if (tmp[key] === null || tmp[key] === undefined) {
                delete tmp[key];
            }
            if (typeof tmp[key] === 'object' && _.isEmpty(tmp[key])) {
                delete tmp[key];
            }
        });

        return tmp;
    };

    // Returns an object that contains only the values that are different between two objects
    Vue.prototype.$utils.getObjectsDifference = (
        currentObject,
        checkedAgainst,
        removeFieldsWithEmptyArrays = true,
    ) => {
        const tmp = _.cloneDeep(currentObject);

        // Iterate through the object
        Object.keys(tmp).forEach(key => {
            // If the key is an object
            if (_.isPlainObject(tmp[key])) {
                // If checkedAgainst has the key as well, we can do what is in the block
                if (Object.keys(checkedAgainst).includes(key)) {
                    // We do recursion
                    tmp[key] = {
                        ...Vue.prototype.$utils.getObjectsDifference(
                            tmp[key],
                            checkedAgainst[key],
                        ),
                    };

                    // If the resulting object is empty, we remove it
                    if (!Object.keys(tmp[key]).length) {
                        delete tmp[key];
                    }
                }
            } else if (Array.isArray(tmp[key])) {
                // Iterate through the array
                tmp[key].forEach((item, index) => {
                    // If checkedAgainst has the key as well, we can do what is in the block
                    // &&
                    // If they share the same value, we remove it from the array
                    if (
                        Object.keys(checkedAgainst).includes(key)
                        && checkedAgainst[key].includes(item)
                    ) {
                        tmp[key].splice(index, 1);
                    }
                });
                // If the array is empty, we remove it
                if (removeFieldsWithEmptyArrays && !tmp[key].length) {
                    delete tmp[key];
                }
            } else if (
                Object.keys(checkedAgainst).includes(key)
                && tmp[key] === checkedAgainst[key]
            ) {
                // If checkedAgainst has the key as well, we can do what is in the block
                // &&
                // If they share the same value, we remove it
                delete tmp[key];
            }
        });
        return tmp;
    };

    Vue.prototype.$utils.getColorsPalette = (numberOfColors = 0) => {
        let colorsPalette = { ...colors };
        const numberOfBaseColors = Object.values(colorsPalette).length;
        // Removing grey and shades
        delete colorsPalette.shades;
        delete colorsPalette.red;
        delete colorsPalette.amber;
        delete colorsPalette.grey;
        delete colorsPalette.brown;
        delete colorsPalette.blueGrey;
        delete colorsPalette.yellow;
        delete colorsPalette.green;
        delete colorsPalette.lightGreen;
        delete colorsPalette.lime;

        // get values from object to manipulate
        // randomize the order of color
        colorsPalette = Object.values(colorsPalette);
        // get the base colors
        const colorsToReturn = colorsPalette.map(color => color.base);

        // check how many unique colors are needed
        if (numberOfBaseColors ** 2 > numberOfColors) {
            colorsToReturn.concat(colorsPalette.map(color => color.lighten1));
        }
        if (numberOfBaseColors ** 3 > numberOfColors) {
            colorsToReturn.concat(colorsPalette.map(color => color.darken1));
        }

        colorsToReturn.sort(() => Math.random() - 0.5);
        return colorsToReturn.slice(0, numberOfColors);
    };

    Vue.prototype.$utils.getRandomInt = (max = 100) => Math.floor(Math.random() * Math.floor(max));

    Vue.prototype.$utils.isDateEqualDate = (date, dateToCompare) => moment(date).isSame(dateToCompare, 'day');
    Vue.prototype.$utils.isBetweenDates = (date, dateRange) => {
        const range = dateRange.map(d => new Date(d));
        return moment(date).isBetween(range[0], range[1], 'day', '[]');
    };
    Vue.prototype.$utils.getDateOnlyFromISOString = date => (!date ? '-' : moment(new Date(date)).format('LL'));
    Vue.prototype.$utils.getCurrentDate = () => moment().format();
    Vue.prototype.$utils.getTomorrowDate = () => moment()
        .add(1, 'days')
        .format();
    Vue.prototype.$utils.exportAsCSV = (data, headers) => {
        // Create a deep to copy to manipulate
        const fields = _.cloneDeep(headers);

        // Remove ID if already exists
        const idIndex = fields.indexOf('id');
        if (idIndex !== -1) {
            fields.splice(idIndex, 1);
        }

        fields.unshift('id');

        const dataCsv = data
            .map(row => {
                const item = _.cloneDeep(row);

                // We get the value from the rawHtml object
                if (item.rawHtml) {
                    item[item.rawHtml.prop] = item.rawHtml.rawValue;
                }

                return (
                    Object.keys(item)
                        // We return only what the user is seeing
                        .filter(key => fields.includes(key))
                        // For specific columns, we can have an Object as a value,
                        // so we try to access the key 'value', of simply "dump" the object as string
                        .map(key => {
                            let value;

                            if (!_.isPlainObject(item[key])) {
                                value = item[key];
                            } else {
                                value = item[key].value
                                    ? item[key].value
                                    : JSON.stringify(item[key]);
                            }

                            return { [key]: value };
                        })
                        // We return back the object with all props
                        .reduce((res, o) => Object.assign(res, o), {})
                );
            })
            // Add an incremental id to the object
            .map((item, index) => ({
                id: index.toString(),
                ...item,
            }));
        // Download the csv to the download location of the browser
        return csvDownload(dataCsv);
    };
    Vue.prototype.$utils.exportSimpleCSV = (header, data, title, type, detail) => {
        const rows = [header];
        const escapeCsvValue = value => {
            if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
                return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
        };
        if (type === 'graph') {
            data.forEach(series => {
                if (series.data.length > 0) {
                    series.data.forEach(item => {
                        rows.push([
                            escapeCsvValue(series.name),
                            escapeCsvValue(item.measuredAt),
                            escapeCsvValue(item.value),
                            escapeCsvValue(item.metadata.unit),
                        ]);
                    });
                }
            });
        }
        if (type === 'billingRegister') {
            data.forEach(series => {
                if (detail === 'sites') {
                    rows.push([
                        escapeCsvValue(series.serviceProvider),
                        escapeCsvValue(series.clientNumber),
                        escapeCsvValue(series.clientName),
                        escapeCsvValue(series.siteNumber),
                        escapeCsvValue(series.siteName),
                        escapeCsvValue(series.linkedUsers),
                        escapeCsvValue(series.linkedDevices),
                    ]);
                }
                if (detail === 'users') {
                    rows.push([
                        escapeCsvValue(series.serviceProvider),
                        escapeCsvValue(series.firstName),
                        escapeCsvValue(series.lastName),
                        escapeCsvValue(series.email),
                        escapeCsvValue(series.phone),
                        escapeCsvValue(series.role),
                        escapeCsvValue(series.memberDate),
                    ]);
                }
            });
        }
        const csvContent = rows.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', title);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    Vue.prototype.$utils.saveFileOnDisk = (blob, fileName) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        link.click();
    };

    /**
     * Detect if a string is inside another string.
     * Useful for our custom "Search" in DataTables
     *
     * @param {String} search - The text to search for
     * @param {String} container - The text to search in
     * @return {Boolean}
     */
    Vue.prototype.$utils.hasString = (search, container) => {
        if (typeof container !== 'string') {
            return false;
        }

        return container.toLowerCase().includes(search.toLowerCase());
    };

    Vue.prototype.$utils.isFloat = n => Number(n) === n && n % 1 !== 0;
    Vue.prototype.$utils.isInt = n => Number(n) === n && n % 1 === 0;
    Vue.prototype.$utils.isNumber = n => Number(n) === n;
    Vue.prototype.$utils.ascSort = (tab, key = 'name') => tab.sort((a, b) => a[key].localeCompare(b[key]));

    Vue.prototype.$utils.isBetween = (value, min, max, inclusive = true) => {
        if (inclusive) {
            return value >= min && value <= max;
        }
        return value > min && value < max;
    };
    Vue.prototype.$utils.formatFloat = value => {
        // eslint-disable-next-line no-restricted-globals
        if (value === null || value === undefined || isNaN(value)) return '';
        const num = Number(value);
        return num.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    };
};

export default UtilsPlugin;
